/**
 * @file  src\pages\AgentDashboard.js
 * @brief  This file is responsible for agent dashboard section
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import { Tabs, Tab, Badge } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Table from "react-bootstrap/Table";
import AddNewClientModal from "@/components/MemberWantAd/AddNewClientModal";
import DeleteModal from "@/components/Common/DeleteModal";
import { useNavigate } from "react-router-dom";
import WantAdListing from "@/components/AgentDashboard/WantAdListing";
import InventoryListing from "@/components/AgentDashboard/InventoryListing";
import {
    getClientsList,
    getAgentDashboard,
    saveAgentClient,
    getAgentWantAdList,
    getAgentInventoryList,
} from "@/services/agentClientsService";
import { getWantAdById, deleteWantAd } from "@/services/wantAdService";
import {
    getInventoryById,
    deleteMyInventory,
} from "@/services/inventoryService";
import TextBox from "@/components/Common/TextBox";
import moment from "moment";
import { toast } from "react-toastify";
import {
    getLocalStorage,
    setLocalStorage,
    truncate,
    checkTutorialDisplayStatus,
    saveTutorialStatusToLocal,
} from "@/helpers/common";
import { pageURLs } from "@/constants/pageURLs";
import {
    CLIENT_DETAILS_KEY,
    NOT_FREE_OR_MEMBER_PERIOD,
    PAGE_SIZE,
    USER_INFO_KEY,
} from "@/constants/common";
import JoyRide, { STATUS } from "react-joyride";
import DocumentationTutorial from "@/components/Tutorial/DocumentationTutorial";
import AgentDashboardModal from "@/components/Tutorial/AgentDashboardModal";
import { saveTutorialStatus } from "@/services/commonServices";
import IntroductionTutorialIconStep1 from "../images/tut-quickview-icon.svg";
import IntroductionTutorialIconStep2 from "../images/tut-performing-icon.svg";
import IntroductionTutorialIconStep3 from "../images/tut-easilyadd-icon.svg";
import _ from "lodash";
import { PageWrapper } from "@/components/PageWrapper";

const AgentDashboard = (props) => {
    const tutorialPage = 4; //Agent Dashboard
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [dashboardCount, setDashboardCount] = useState([]);
    const [itemList, setList] = useState([]); // storing list
    const [searchValue, setSearchValue] = useState(null);
    const [clientAdded, setClientAdded] = useState(false);
    const [showNoResult, setShowNoResult] = useState(false);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [deleteStatus, setDeleteStatus] = useState(false);
    const userDetailsFromStore = useSelector(
        (state) => state.userProfile.userDetails
    );
    const [itemId, setItemId] = useState(0);
    const CLIENT_TAB = "client";
    const INVENTORY_TAB = "inventory";
    const WANT_AD_TAB = "wantad";
    const [activeTab, setActiveTab] = useState(CLIENT_TAB);
    const [displayTutorial, setDisplayTutorial] = useState(false);
    const [tutorialModalShow, setTutorialModalShow] = useState(false);
    const [tutorialAccept, setTutorialAccept] = useState(false);

    // column name for sorting userlisting
    const columns = [
        { accessor: "firstName", label: "Client", sorting: "Yes" },
        { accessor: "email", label: "Email", sorting: "Yes" },
        { accessor: "createdOn", label: "Date added", sorting: "Yes" },
        { accessor: "inventory", label: "Inventory", sorting: "No" },
        { accessor: "wantAds", label: "Want Listings", sorting: "No" },
        { accessor: "introSent", label: "Intros sent", sorting: "No" },

        { accessor: "introSent1", label: "Intros received", sorting: "No" },
    ];
    const [sort, setSort] = useState({ order: "desc", orderBy: "CreatedOn" });
    const [wantAdSort, setWantAdSort] = useState({
        order: "asc",
        orderBy: "date",
    });
    const [inventorySort, setInventorySort] = useState({
        order: "asc",
        orderBy: "date",
    });

    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [inventoryPage, setInventoryPage] = useState(1);
    const [wantAdPage, setWantAdPage] = useState(1);
    const [tabSwitched, setTabSwitched] = useState(false);

    useEffect(() => {
        if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
            if (
                userDetailsFromStore[0]?.period &&
                userDetailsFromStore[0]?.period === NOT_FREE_OR_MEMBER_PERIOD
            ) {
                navigate(pageURLs.home);
            }
        }
    }, [userDetailsFromStore]);
    /**
     * To handle scroll
     */
    const handleScroll = useCallback(() => {
        if (
            window.innerHeight + document.documentElement.scrollTop >=
            document.documentElement.offsetHeight - 50 &&
            !loading &&
            hasMore
        ) {
            if (activeTab == CLIENT_TAB && !tabSwitched) {
                setPage((prevPage) => prevPage + 1);
            } else if (activeTab == INVENTORY_TAB && !tabSwitched) {
                setInventoryPage((prevPage) => prevPage + 1);
            } else if (activeTab == WANT_AD_TAB && !tabSwitched) {
                setWantAdPage((prevWantAdPage) => prevWantAdPage + 1);
            }
        }
    }, [loading, hasMore]);

    useEffect(() => {
        const debouncedHandleScroll = _.debounce(handleScroll, 200);
        window.addEventListener("scroll", debouncedHandleScroll);
        return () => {
            window.removeEventListener("scroll", debouncedHandleScroll);
        };
    }, [handleScroll]);
    /**
     * To handle tab selection
     * @param {*} tab
     */
    const handleTabSelection = (tab) => {
        if (tab !== activeTab) {
            setTabSwitched(true);
            if (tab == INVENTORY_TAB) {
                setInventoryPage(1);
            } else if (tab == WANT_AD_TAB) {
                setWantAdPage(1);
            } else if (tab == CLIENT_TAB) {
                setPage(1);
            }

            setHasMore(true);
            setLoading(false);
            setSearchValue("");
            if (activeTab !== tab) {
                setActiveTab(tab);
                setList([]);
            }
        }
    };

    /**
     * function to call client list api
     * @param {*} data
     */
    const fetchData = useCallback(
        async (searchValue, sort) => {
            if (loading) return;
            setLoading(true);
            setTabSwitched(false);
            try {
                const data = {
                    sortBy: sort.orderBy,
                    search: searchValue,
                    sortDir: sort.order,
                    page: page,
                    limit: PAGE_SIZE,
                };
                await getClientsList(data)
                    .then((response) => {
                        const responseResult = _.get(response, "result", null);
                        if (response.errorCode === 0) {
                            setShowNoResult(true);
                            if (responseResult.length > 0) {
                                setList((prev) => [...prev, ...responseResult]);
                                if (checkTutorialDisplayStatus(tutorialPage)) {
                                    setTutorialModalShow(true);
                                }
                            } else {
                                if (itemList.length == 0) {
                                    setTutorialModalShow(false);
                                }
                                setHasMore(false);
                            }
                        } else {
                            setShowNoResult(false);
                        }
                    })
                    .catch((err) => {
                        console.log("Error", err);
                    });
            } catch (error) {
                console.error("Failed to fetch items:", error);
            } finally {
                setLoading(false);
            }
        },
        [page]
    );

    useEffect(() => {
        if (activeTab === CLIENT_TAB) {
            fetchData(searchValue, sort);
        }
    }, [fetchData, activeTab, searchValue, clientAdded, sort]);
    /**
     * To fetch want ad data
     */
    const fetchWantAdData = useCallback(
        async (searchValue, wantAdSort) => {
            if (loading) return;
            setLoading(true);
            setTabSwitched(false);
            try {
                const data = {
                    sortBy: wantAdSort.orderBy,
                    search: searchValue,
                    sortDir: wantAdSort.order,
                    page: wantAdPage,
                    limit: PAGE_SIZE,
                };
                await getAgentWantAdList(data)
                    .then((response) => {
                        const responseResult = _.get(response, "result", null);
                        if (response.errorCode === 0) {
                            setShowNoResult(true);
                            if (responseResult.length > 0) {
                                setList((prev) => [...prev, ...responseResult]);
                            } else {
                                setHasMore(false);
                            }
                        } else {
                            setShowNoResult(false);
                        }
                    })
                    .catch((err) => {
                        console.log("Error", err);
                    });
            } catch (error) {
                console.error("Failed to fetch items:", error);
            } finally {
                setLoading(false);
            }
        },
        [wantAdPage]
    );

    useEffect(() => {
        if (activeTab === WANT_AD_TAB) {
            fetchWantAdData(searchValue, wantAdSort);
        }
    }, [fetchWantAdData, activeTab, searchValue, wantAdSort, clientAdded]);
    /**
     * To fetch inventory data
     */
    const fetchInventoryData = useCallback(
        async (searchValue, inventorySort) => {
            if (loading) return;
            setLoading(true);
            setTabSwitched(false);
            try {
                const data = {
                    sortBy: inventorySort.orderBy,
                    search: searchValue,
                    sortDir: inventorySort.order,
                    page: inventoryPage,
                    limit: PAGE_SIZE,
                };
                await getAgentInventoryList(data)
                    .then((response) => {
                        const responseResult = _.get(response, "result", null);
                        if (response.errorCode === 0) {
                            setShowNoResult(true);
                            const list = responseResult?.list ?? responseResult;
                            if (Array.isArray(list) && list.length > 0) {
                                setList((prev) => [...prev, ...list]);
                            } else {
                                setHasMore(false);
                            }
                        } else {
                            setShowNoResult(false);
                        }
                    })
                    .catch((err) => {
                        console.log("Error", err);
                    });
            } catch (error) {
                console.error("Failed to fetch items:", error);
            } finally {
                setLoading(false);
            }
        },
        [inventoryPage]
    );

    useEffect(() => {
        if (activeTab === INVENTORY_TAB) {
            fetchInventoryData(searchValue, inventorySort);
        }
    }, [fetchInventoryData, activeTab, searchValue, clientAdded, inventorySort]);

    /**
     * on searching the group
     * @param event any
     */
    const onSearch = (event) => {
        event.preventDefault();
        setSearchValue(event.target.value);
        setTabSwitched(true);
        setLoading(false);
        if (activeTab == INVENTORY_TAB) {
            setInventoryPage(1);
        } else if (activeTab == WANT_AD_TAB) {
            setWantAdPage(1);
        } else if (activeTab == CLIENT_TAB) {
            setPage(1);
        }
        setList([]);
    };

    /**
     * To prevent the enter key page reloading
     * @param event any
     */
    const onKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    };

    /**
     * to handle sort columns
     * @param {string} accessor
     */
    const handleSort = (accessor) => {
        setSort((prevSort) => ({
            order:
                prevSort.order === "asc" && prevSort.orderBy === accessor
                    ? "desc"
                    : "asc",
            orderBy: accessor,
        }));

        setTabSwitched(true);
        setPage(1);
        setHasMore(true);
        setLoading(false);
        setList([]);
    };

    const handleWantAdSort = (sortKey) => {
        setWantAdSort((prevSort) => ({
            order:
                prevSort.order === "asc" && prevSort.orderBy === sortKey
                    ? "desc"
                    : "asc",
            orderBy: sortKey,
        }));
        setTabSwitched(true);
        setWantAdPage(1);
        setHasMore(true);
        setLoading(false);
        setList([]);
    };

    const handleInventorySort = (sortKey) => {
        setInventorySort((prevSort) => ({
            order:
                prevSort.order === "asc" && prevSort.orderBy === sortKey
                    ? "desc"
                    : "asc",
            orderBy: sortKey,
        }));

        setTabSwitched(true);
        setInventoryPage(1);
        setHasMore(true);
        setLoading(false);
        setList([]);
    };

    const onItemDelete = (itemId) => {
        setItemId(itemId);
        setDeleteModalShow(true);
    };

    const onItemEdit = (itemId) => {
        getDetails(itemId);
    };
    /**
     * To get inventory details and navigate to edit screen
     * @param {*} itemId
     */
    const getDetails = async (itemId) => {
        if (itemId) {
            if (activeTab === INVENTORY_TAB) {
                await getInventoryById(itemId)
                    .then((response) => {
                        const responseResult = _.get(response, "result", null);
                        if (response.errorCode === 0 && responseResult) {
                            navigate(pageURLs.agentCreateInventory, {
                                state: { inventoryId: itemId, data: responseResult },
                            });
                        } else {
                            console.log("Something went wrong");
                        }
                    })
                    .catch((err) => {
                        console.log("Error", err);
                    });
            } else {
                await getWantAdById(itemId)
                    .then((response) => {
                        const responseResult = _.get(response, "result", null);
                        if (response.errorCode === 0 && responseResult) {
                            navigate(`${pageURLs.editWantAdForAgents}/${responseResult.wantAdId}`, {
                                state: { data: responseResult },
                            });
                        } else {
                            console.log("Something went wrong");
                        }
                    })
                    .catch((err) => {
                        console.log("Error", err);
                    });
            }
        } else {
            navigate(pageURLs.agentCreatewantad);
        }
    };

    const hideDeleteModal = () => {
        setDeleteModalShow(false);
    };
    /**
     * To handle deletion if inventory/wantad
     */
    const handleDeleteItem = async () => {
        if (itemId) {
            if (activeTab === INVENTORY_TAB) {
                await deleteMyInventory(itemId)
                    .then((response) => {
                        const responseResult = _.get(response, "result", null);
                        if (response.errorCode === 0 && responseResult) {
                            toast.success("Your inventory has been successfully deleted.");
                            setDeleteModalShow(false);
                            fetchCount();
                            setList((current) =>
                                current.filter((lst) => lst.Inv_Id !== itemId)
                            );
                        } else {
                            console.log("Something went wrong");
                        }
                    })
                    .catch((err) => {
                        console.log("Error", err);
                    });
            } else {
                await deleteWantAd(itemId)
                    .then((response) => {
                        const responseResult = _.get(response, "result", null);
                        if (response.errorCode === 0 && responseResult) {
                            toast.success("Your Want-Listing has been successfully deleted.");
                            setDeleteModalShow(false);
                            fetchCount();
                            setList((current) =>
                                current.filter((lst) => lst.Want_ad_Id !== itemId)
                            );
                        } else {
                            console.log("Something went wrong");
                        }
                    })
                    .catch((err) => {
                        console.log("Error", err);
                    });
            }
            setShowNoResult(true);
        }
    };

    useEffect(() => {
        fetchCount();
    }, [clientAdded]);

    useEffect(() => {
        if (itemList.length > 0) {
            setShowNoResult(true);
        }
    }, [itemList]);

    /**
     * function to call count api
     * @param {*} data
     */
    const fetchCount = async () => {
        await getAgentDashboard()
            .then((response) => {
                const responseResult = _.get(response, "result", null);
                if (response.errorCode === 0 && responseResult) {
                    setDashboardCount(responseResult);
                } else {
                    console.log("Something went wrong");
                }
            })
            .catch((err) => {
                console.log("Error", err);
            });
    };

    /**
     * add client
     * @param {json} data
     */
    const addClientData = async (data) => {
        var data = {
            ...data,
        };
        setClientAdded(false);
        await saveAgentClient(data)
            .then((response) => {
                if (response.errorCode === 0) {
                    const userInfo = getLocalStorage(USER_INFO_KEY);
                    userInfo.userInfo["ClientCount"] = userInfo.userInfo?.ClientCount
                        ? userInfo.userInfo?.ClientCount + 1
                        : 1;
                    setLocalStorage(USER_INFO_KEY, userInfo);

                    if (response.result.agentExist) {
                        toast.error(response.message);
                    } else {
                        toast.success("Client added successfully");
                        setClientAdded(!clientAdded);
                        setList([]);
                        setShow(false);
                        setTabSwitched(true);
                        setPage(1);
                        setActiveTab(CLIENT_TAB);
                        setHasMore(true);
                        setLoading(false);
                    }
                } else {
                    return;
                }
            })
            .catch((err) => {
                console.log("Error", err);
            });
    };

    const dashboardHeaderSection = (props) => {
        const { placeHolder, activeTab } = props;
        return (
            <div className="dashboard-top-bar">
                <div className="dashboard-search">
                    <TextBox
                        type="text"
                        className="form-control"
                        placeholder={placeHolder}
                        onChange={onSearch}
                        onKeyDown={onKeyDown}
                        value={searchValue ?? ""}
                    ></TextBox>

                    <span className="search-icon">search</span>
                </div>
                <Dropdown as={ButtonGroup}>
                    <Button
                        variant="primary"
                        className="split-btn-margin text-nowrap ms-3"
                        onClick={handleShow}
                    >
                        Add Client
                    </Button>

                    <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />

                    <Dropdown.Menu>
                        <Dropdown.Item
                            onClick={() => navigate(pageURLs.agentCreateInventory)}
                        >
                            <span>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8 0.0898438C7.52138 0.0898438 7.04293 0.245532 6.64746 0.556966H6.64665L1.69141 4.4624C0.939922 5.05464 0.5 5.95958 0.5 6.91683V14.4535C0.5 15.2513 1.16044 15.9118 1.95833 15.9118H14.0417C14.8396 15.9118 15.5 15.2513 15.5 14.4535V6.91683C15.5 5.95958 15.0601 5.05464 14.3086 4.4624L14.25 4.4152V1.125C14.25 0.959245 14.1841 0.800283 14.0669 0.683077C13.9497 0.56587 13.7908 0.500017 13.625 0.5H11.5417C11.3759 0.500017 11.2169 0.56587 11.0997 0.683077C10.9825 0.800283 10.9167 0.959245 10.9167 1.125V1.78906L9.35254 0.556966C8.95707 0.245532 8.47862 0.0898438 8 0.0898438ZM8 1.33659C8.20408 1.33659 8.40823 1.40441 8.57943 1.53923L11.1551 3.56803C11.2473 3.64054 11.358 3.68566 11.4746 3.69824C11.5911 3.71082 11.7089 3.69035 11.8144 3.63917C11.9199 3.58798 12.0089 3.50815 12.0712 3.4088C12.1335 3.30945 12.1666 3.19458 12.1667 3.07731V1.75H13V4.71875C13.0001 4.81325 13.0216 4.9065 13.0629 4.9915C13.1042 5.0765 13.1642 5.15102 13.2384 5.20947L13.5355 5.44385C13.9873 5.79994 14.25 6.34158 14.25 6.91683V14.4535C14.25 14.5764 14.1646 14.6618 14.0417 14.6618H1.95833C1.8354 14.6618 1.75 14.5764 1.75 14.4535V6.91683C1.75 6.34158 2.01267 5.79994 2.46452 5.44385L7.42057 1.53923C7.59177 1.40441 7.79592 1.33659 8 1.33659ZM4.04167 7.99512C3.87591 7.99513 3.71695 8.06099 3.59974 8.17819C3.48254 8.2954 3.41668 8.45436 3.41667 8.62012V11.9535C3.41668 12.1192 3.48254 12.2782 3.59974 12.3954C3.71695 12.5126 3.87591 12.5784 4.04167 12.5785H6.54167C6.70742 12.5784 6.86638 12.5126 6.98359 12.3954C7.1008 12.2782 7.16665 12.1192 7.16667 11.9535V8.62012C7.16665 8.45436 7.1008 8.2954 6.98359 8.17819C6.86638 8.06099 6.70742 7.99513 6.54167 7.99512H4.04167ZM9.45833 7.99512C9.29258 7.99513 9.13362 8.06099 9.01641 8.17819C8.8992 8.2954 8.83335 8.45436 8.83333 8.62012V11.9535C8.83335 12.1192 8.8992 12.2782 9.01641 12.3954C9.13362 12.5126 9.29258 12.5784 9.45833 12.5785H11.9583C12.1241 12.5784 12.283 12.5126 12.4003 12.3954C12.5175 12.2782 12.5833 12.1192 12.5833 11.9535V8.62012C12.5833 8.45436 12.5175 8.2954 12.4003 8.17819C12.283 8.06099 12.1241 7.99513 11.9583 7.99512H9.45833ZM4.66667 9.24512H5.91667V11.3285H4.66667V9.24512ZM10.0833 9.24512H11.3333V11.3285H10.0833V9.24512Z"
                                        fill="#15181E"
                                    />
                                </svg>
                            </span>
                            Add inventory
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => navigate(pageURLs.agentCreatewantadMainScreen)}
                        >
                            <span>
                                <svg
                                    width="18"
                                    height="16"
                                    viewBox="0 0 18 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M1.26407 1.05319C1.49849 0.818769 1.81643 0.687073 2.14795 0.687073H15.8979C16.2295 0.687073 16.5474 0.818769 16.7818 1.05319C17.0163 1.28761 17.1479 1.60555 17.1479 1.93707V14.4371C17.1479 14.6537 17.0358 14.8549 16.8515 14.9687C16.6673 15.0826 16.4372 15.093 16.2434 14.9961L14.0229 13.8858L11.8025 14.9961C11.6265 15.0841 11.4194 15.0841 11.2434 14.9961L9.02295 13.8858L6.80246 14.9961C6.6265 15.0841 6.41939 15.0841 6.24344 14.9961L4.02295 13.8858L1.80246 14.9961C1.60871 15.093 1.37863 15.0826 1.19437 14.9687C1.01011 14.8549 0.897949 14.6537 0.897949 14.4371V1.93707C0.897949 1.60555 1.02965 1.28761 1.26407 1.05319ZM15.8979 1.93707L2.14795 1.93707L2.14795 13.4258L3.74344 12.6281C3.9194 12.5401 4.1265 12.5401 4.30246 12.6281L6.52295 13.7383L8.74344 12.6281C8.9194 12.5401 9.1265 12.5401 9.30246 12.6281L11.5229 13.7383L13.7434 12.6281C13.9194 12.5401 14.1265 12.5401 14.3025 12.6281L15.8979 13.4258V1.93707Z"
                                        fill="#333A48"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M9.64795 6.31207C9.64795 5.9669 9.92777 5.68707 10.2729 5.68707H14.0229C14.3681 5.68707 14.6479 5.9669 14.6479 6.31207C14.6479 6.65725 14.3681 6.93707 14.0229 6.93707H10.2729C9.92777 6.93707 9.64795 6.65725 9.64795 6.31207Z"
                                        fill="#333A48"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M9.64795 8.81207C9.64795 8.4669 9.92777 8.18707 10.2729 8.18707H14.0229C14.3681 8.18707 14.6479 8.4669 14.6479 8.81207C14.6479 9.15725 14.3681 9.43707 14.0229 9.43707H10.2729C9.92777 9.43707 9.64795 9.15725 9.64795 8.81207Z"
                                        fill="#333A48"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.39795 5.06207C3.39795 4.7169 3.67777 4.43707 4.02295 4.43707H7.77295C8.11813 4.43707 8.39795 4.7169 8.39795 5.06207V10.0621C8.39795 10.4073 8.11813 10.6871 7.77295 10.6871H4.02295C3.67777 10.6871 3.39795 10.4073 3.39795 10.0621V5.06207ZM4.64795 5.68707V9.43707H7.14795V5.68707H4.64795Z"
                                        fill="#333A48"
                                    />
                                </svg>
                            </span>
                            Post a want-listing
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    const emptyItemHtml = () => {
        if (!loading && showNoResult) {
            return (
                <div className="no-result-wrap no-result-padding">
                    <h3>No results found</h3>
                    <p>
                        We couldn't find any listings that match your filters and search
                        terms. Try a different search or reset all filters.
                    </p>
                </div>
            );
        }
    };

    const handleClientClick = (clientId) => {
        setLocalStorage(CLIENT_DETAILS_KEY, { id: clientId });
        navigate(pageURLs.viewClientDetails);
    };

    let deleteButtonProps = {};

    if (activeTab === INVENTORY_TAB) {
        _.set(
            deleteButtonProps,
            "title",
            "Are you sure you want to permanently delete your property?"
        );
        _.set(
            deleteButtonProps,
            "message",
            "Deleting this property from your inventory will remove any pending and accepted introductions sent by you."
        );
        _.set(deleteButtonProps, "btnText", " Delete Property");
    } else if (activeTab === WANT_AD_TAB) {
        _.set(
            deleteButtonProps,
            "title",
            "Are you sure you want to permanently delete your want-listing?"
        );
        _.set(
            deleteButtonProps,
            "message",
            "Deleting this listing will stop any further introduction from being sent to you."
        );
        _.set(deleteButtonProps, "btnText", " Delete Want-Listing");
    }

    //Tutorial documentation section
    //Defining tutorial steps
    const [tutorialSteps, setTutorialSteps] = useState([
        {
            target: ".tutorialRow",
            disableBeacon: true,
            placement: "left",
            title: "Quickly view all of your clients information",
            icon: IntroductionTutorialIconStep1,
            content:
                "You can view a summary here or select your client to view their full set of inventory and want listings.",
            hideCloseButton: true,
            showProgress: true,
            showSkipButton: true,
        },
        {
            target: ".tutorialRow",
            disableBeacon: true,
            placement: "top",
            title: "Figure out which listings / properties are performing",
            icon: IntroductionTutorialIconStep2,
            content:
                "This will help you modify your listings and inventory to figure out what strategies are worth replicating.",
            hideCloseButton: true,
            showProgress: true,
            showSkipButton: true,
        },
        {
            target: "#clientAdd",
            disableBeacon: true,
            placement: "left",
            title: "Easily add",
            icon: IntroductionTutorialIconStep3,
            content:
                "New clients, new inventory, and new want listings from one spot",
            hideCloseButton: true,
            showProgress: true,
            showSkipButton: true,
            spotlightPadding: false,
        },
    ]);
    //Tutorial tooltip
    const TutorialToolTip = ({
        continuous,
        index,
        step,
        backProps,
        closeProps,
        primaryProps,
        tooltipProps,
        size,
        isLastStep,
        skipProps,
    }) => (
        <>
            <div {...tooltipProps}>
                <DocumentationTutorial
                    continuous={continuous}
                    index={index}
                    step={step}
                    backProps={backProps}
                    skipProps={skipProps}
                    primaryProps={primaryProps}
                    isLastStep={isLastStep}
                    size={size}
                    finalStepBtn={"Done"}
                />
            </div>
        </>
    );
    //Tutorial status call back
    const handleJoyrideCallback = async (data) => {
        const { action, index, status, type, lifecycle } = data;
        if (
            [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
            lifecycle == "complete"
        ) {
            setTutorialAccept(true);
            saveTutorialStatusToLocal(tutorialPage);
            await saveTutorialStatus(tutorialPage, 1); //Save status
            setDisplayTutorial(false);
        }
        if (action == "update" && index == 2 && status == "running") {
            document.querySelector("#dropdown-split-basic").click();
        }
    };

    const hideTutorialModal = async () => {
        setTutorialModalShow(false);
        saveTutorialStatusToLocal(tutorialPage);
        setTutorialAccept(true);
        await saveTutorialStatus(tutorialPage, 1); //Save status
    };

    const startTutorial = () => {
        setTutorialModalShow(false);
        setDisplayTutorial(true);
    };
    return (
        <>
            {displayTutorial && itemList !== null && itemList?.length ? (
                <JoyRide
                    steps={tutorialSteps}
                    tooltipComponent={TutorialToolTip}
                    continuous
                    callback={handleJoyrideCallback}
                />
            ) : (
                ""
            )}

            <PageWrapper>
                <div className="m-0">
                    <div className="main-content p-0 agent-no-login-content">
                        <div className="agent-dashboard-wrap mx-3">
                            <h2 className="mb-3">Agent dashboard</h2>
                            <div className="horizontal-tab-wrap">
                                <Tabs
                                    id="controlled-tab-example1"
                                    className="mb-0"
                                    unmountOnExit
                                    activeKey={activeTab}
                                    onSelect={(k) => handleTabSelection(k)}
                                >
                                    <Tab
                                        eventKey={CLIENT_TAB}
                                        title={
                                            <React.Fragment>
                                                Clients
                                                <Badge>{dashboardCount?.client}</Badge>
                                            </React.Fragment>
                                        }
                                    >
                                        <div className="dashboard-top-bar">
                                            <div className="dashboard-search">
                                                <TextBox
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search for client"
                                                    onChange={onSearch}
                                                    onKeyDown={onKeyDown}
                                                    value={searchValue ?? ""}
                                                ></TextBox>
                                                <span className="search-icon">search</span>
                                            </div>
                                            <Dropdown as={ButtonGroup} id="clientAdd">
                                                <Button
                                                    variant="primary"
                                                    className="split-btn-margin text-nowrap ms-3 ms-sm-0"
                                                    onClick={handleShow}
                                                >
                                                    Add Client
                                                </Button>

                                                <Dropdown.Toggle
                                                    split
                                                    variant="primary"
                                                    id="dropdown-split-basic"
                                                />

                                                <Dropdown.Menu isOpen={true}>
                                                    <Dropdown.Item
                                                        onClick={() =>
                                                            navigate(pageURLs.agentCreateInventory)
                                                        }
                                                    >
                                                        <span>
                                                            <svg
                                                                width="16"
                                                                height="16"
                                                                viewBox="0 0 16 16"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M8 0.0898438C7.52138 0.0898438 7.04293 0.245532 6.64746 0.556966H6.64665L1.69141 4.4624C0.939922 5.05464 0.5 5.95958 0.5 6.91683V14.4535C0.5 15.2513 1.16044 15.9118 1.95833 15.9118H14.0417C14.8396 15.9118 15.5 15.2513 15.5 14.4535V6.91683C15.5 5.95958 15.0601 5.05464 14.3086 4.4624L14.25 4.4152V1.125C14.25 0.959245 14.1841 0.800283 14.0669 0.683077C13.9497 0.56587 13.7908 0.500017 13.625 0.5H11.5417C11.3759 0.500017 11.2169 0.56587 11.0997 0.683077C10.9825 0.800283 10.9167 0.959245 10.9167 1.125V1.78906L9.35254 0.556966C8.95707 0.245532 8.47862 0.0898438 8 0.0898438ZM8 1.33659C8.20408 1.33659 8.40823 1.40441 8.57943 1.53923L11.1551 3.56803C11.2473 3.64054 11.358 3.68566 11.4746 3.69824C11.5911 3.71082 11.7089 3.69035 11.8144 3.63917C11.9199 3.58798 12.0089 3.50815 12.0712 3.4088C12.1335 3.30945 12.1666 3.19458 12.1667 3.07731V1.75H13V4.71875C13.0001 4.81325 13.0216 4.9065 13.0629 4.9915C13.1042 5.0765 13.1642 5.15102 13.2384 5.20947L13.5355 5.44385C13.9873 5.79994 14.25 6.34158 14.25 6.91683V14.4535C14.25 14.5764 14.1646 14.6618 14.0417 14.6618H1.95833C1.8354 14.6618 1.75 14.5764 1.75 14.4535V6.91683C1.75 6.34158 2.01267 5.79994 2.46452 5.44385L7.42057 1.53923C7.59177 1.40441 7.79592 1.33659 8 1.33659ZM4.04167 7.99512C3.87591 7.99513 3.71695 8.06099 3.59974 8.17819C3.48254 8.2954 3.41668 8.45436 3.41667 8.62012V11.9535C3.41668 12.1192 3.48254 12.2782 3.59974 12.3954C3.71695 12.5126 3.87591 12.5784 4.04167 12.5785H6.54167C6.70742 12.5784 6.86638 12.5126 6.98359 12.3954C7.1008 12.2782 7.16665 12.1192 7.16667 11.9535V8.62012C7.16665 8.45436 7.1008 8.2954 6.98359 8.17819C6.86638 8.06099 6.70742 7.99513 6.54167 7.99512H4.04167ZM9.45833 7.99512C9.29258 7.99513 9.13362 8.06099 9.01641 8.17819C8.8992 8.2954 8.83335 8.45436 8.83333 8.62012V11.9535C8.83335 12.1192 8.8992 12.2782 9.01641 12.3954C9.13362 12.5126 9.29258 12.5784 9.45833 12.5785H11.9583C12.1241 12.5784 12.283 12.5126 12.4003 12.3954C12.5175 12.2782 12.5833 12.1192 12.5833 11.9535V8.62012C12.5833 8.45436 12.5175 8.2954 12.4003 8.17819C12.283 8.06099 12.1241 7.99513 11.9583 7.99512H9.45833ZM4.66667 9.24512H5.91667V11.3285H4.66667V9.24512ZM10.0833 9.24512H11.3333V11.3285H10.0833V9.24512Z"
                                                                    fill="#15181E"
                                                                />
                                                            </svg>
                                                        </span>
                                                        Add inventory
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        onClick={() =>
                                                            navigate(pageURLs.agentCreatewantadMainScreen)
                                                        }
                                                    >
                                                        <span>
                                                            <svg
                                                                width="18"
                                                                height="16"
                                                                viewBox="0 0 18 16"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    clipRule="evenodd"
                                                                    d="M1.26407 1.05319C1.49849 0.818769 1.81643 0.687073 2.14795 0.687073H15.8979C16.2295 0.687073 16.5474 0.818769 16.7818 1.05319C17.0163 1.28761 17.1479 1.60555 17.1479 1.93707V14.4371C17.1479 14.6537 17.0358 14.8549 16.8515 14.9687C16.6673 15.0826 16.4372 15.093 16.2434 14.9961L14.0229 13.8858L11.8025 14.9961C11.6265 15.0841 11.4194 15.0841 11.2434 14.9961L9.02295 13.8858L6.80246 14.9961C6.6265 15.0841 6.41939 15.0841 6.24344 14.9961L4.02295 13.8858L1.80246 14.9961C1.60871 15.093 1.37863 15.0826 1.19437 14.9687C1.01011 14.8549 0.897949 14.6537 0.897949 14.4371V1.93707C0.897949 1.60555 1.02965 1.28761 1.26407 1.05319ZM15.8979 1.93707L2.14795 1.93707L2.14795 13.4258L3.74344 12.6281C3.9194 12.5401 4.1265 12.5401 4.30246 12.6281L6.52295 13.7383L8.74344 12.6281C8.9194 12.5401 9.1265 12.5401 9.30246 12.6281L11.5229 13.7383L13.7434 12.6281C13.9194 12.5401 14.1265 12.5401 14.3025 12.6281L15.8979 13.4258V1.93707Z"
                                                                    fill="#333A48"
                                                                />
                                                                <path
                                                                    fillRule="evenodd"
                                                                    clipRule="evenodd"
                                                                    d="M9.64795 6.31207C9.64795 5.9669 9.92777 5.68707 10.2729 5.68707H14.0229C14.3681 5.68707 14.6479 5.9669 14.6479 6.31207C14.6479 6.65725 14.3681 6.93707 14.0229 6.93707H10.2729C9.92777 6.93707 9.64795 6.65725 9.64795 6.31207Z"
                                                                    fill="#333A48"
                                                                />
                                                                <path
                                                                    fillRule="evenodd"
                                                                    clipRule="evenodd"
                                                                    d="M9.64795 8.81207C9.64795 8.4669 9.92777 8.18707 10.2729 8.18707H14.0229C14.3681 8.18707 14.6479 8.4669 14.6479 8.81207C14.6479 9.15725 14.3681 9.43707 14.0229 9.43707H10.2729C9.92777 9.43707 9.64795 9.15725 9.64795 8.81207Z"
                                                                    fill="#333A48"
                                                                />
                                                                <path
                                                                    fillRule="evenodd"
                                                                    clipRule="evenodd"
                                                                    d="M3.39795 5.06207C3.39795 4.7169 3.67777 4.43707 4.02295 4.43707H7.77295C8.11813 4.43707 8.39795 4.7169 8.39795 5.06207V10.0621C8.39795 10.4073 8.11813 10.6871 7.77295 10.6871H4.02295C3.67777 10.6871 3.39795 10.4073 3.39795 10.0621V5.06207ZM4.64795 5.68707V9.43707H7.14795V5.68707H4.64795Z"
                                                                    fill="#333A48"
                                                                />
                                                            </svg>
                                                        </span>
                                                        Post a want-listing
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>

                                        <div className="dashboard-table-wrap">
                                            <div className="table-responsive ">
                                                {showNoResult &&
                                                    itemList !== null &&
                                                    itemList?.length > 0 ? (
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                {columns.map((column) => {
                                                                    const sortIcon = () => {
                                                                        if (column.accessor === sort.orderBy) {
                                                                            if (sort.order === "asc") {
                                                                                return "sorting_asc";
                                                                            }
                                                                            return "sorting_desc";
                                                                        } else {
                                                                            return "sorting";
                                                                        }
                                                                    };
                                                                    return (
                                                                        <th key={column.accessor}>
                                                                            {column.sorting == "Yes" && (
                                                                                <span
                                                                                    className={sortIcon() + " first-head"}
                                                                                    onClick={() =>
                                                                                        handleSort(column.accessor)
                                                                                    }
                                                                                >
                                                                                    {column.label}
                                                                                </span>
                                                                            )}
                                                                            {column.sorting == "No" && (
                                                                                <span className="sorting-heading">
                                                                                    {column.label}
                                                                                </span>
                                                                            )}
                                                                        </th>
                                                                    );
                                                                })}
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {itemList.map((clients, i) => {
                                                                return (
                                                                    <tr
                                                                        key={i}
                                                                        className={
                                                                            i == 0
                                                                                ? "tutorialRow"
                                                                                : "clientListingRow"
                                                                        }
                                                                    >
                                                                        <td>
                                                                            <a
                                                                                href={void 0}
                                                                                role="button"
                                                                                className="txt-no-underline client-name-style"
                                                                                onClick={() => {
                                                                                    handleClientClick(
                                                                                        _.get(clients, "ClientID", "")
                                                                                    );
                                                                                }}
                                                                            >
                                                                                {truncate(
                                                                                    clients.FirstName +
                                                                                    " " +
                                                                                    clients.LastName,
                                                                                    15
                                                                                )}
                                                                            </a>
                                                                        </td>

                                                                        <td>{clients.Email}</td>
                                                                        <td>
                                                                            {" "}
                                                                            {clients.CreatedOn
                                                                                ? moment(
                                                                                    new Date(clients.CreatedOn)
                                                                                ).format("MMM Do, YYYY")
                                                                                : ""}
                                                                        </td>

                                                                        <td
                                                                            className={
                                                                                i == 0
                                                                                    ? "performingTutorialRow"
                                                                                    : "TutorialRow"
                                                                            }
                                                                        >
                                                                            <span
                                                                                className={
                                                                                    clients.Inventory > 0
                                                                                        ? "table-badge-blue"
                                                                                        : "table-badge-grey"
                                                                                }
                                                                            >
                                                                                {clients.Inventory + " stored"}
                                                                            </span>
                                                                        </td>
                                                                        <td
                                                                            className={
                                                                                i == 0
                                                                                    ? "performingTutorialRow"
                                                                                    : "TutorialRow"
                                                                            }
                                                                        >
                                                                            <span
                                                                                className={
                                                                                    clients.WantAds > 0
                                                                                        ? "table-badge-blue"
                                                                                        : "table-badge-grey"
                                                                                }
                                                                            >
                                                                                {clients.WantAds + " active"}
                                                                            </span>
                                                                        </td>
                                                                        <td
                                                                            className={
                                                                                i == 0
                                                                                    ? "performingTutorialRow"
                                                                                    : "TutorialRow"
                                                                            }
                                                                        >
                                                                            {clients.IntroSent}
                                                                        </td>

                                                                        <td
                                                                            className={
                                                                                i == 0
                                                                                    ? "performingTutorialRow"
                                                                                    : "TutorialRow"
                                                                            }
                                                                        >
                                                                            {clients.IntroReceived}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                ) : !loading &&
                                                    showNoResult &&
                                                    (searchValue == null || searchValue == "") &&
                                                    activeTab == CLIENT_TAB &&
                                                    itemList &&
                                                    itemList?.length == 0 ? (
                                                    <div className="no-result-wrap no-result-padding">
                                                        <h3>You don’t have any clients</h3>
                                                        <p>
                                                            Use the Add Client button to create your first
                                                            one!
                                                        </p>
                                                    </div>
                                                ) : (
                                                    showNoResult && emptyItemHtml()
                                                )}
                                            </div>
                                        </div>
                                    </Tab>

                                    <Tab
                                        eventKey={INVENTORY_TAB}
                                        title={
                                            <React.Fragment>
                                                Inventory
                                                <Badge>{dashboardCount?.Inventory}</Badge>
                                            </React.Fragment>
                                        }
                                    >
                                        {dashboardHeaderSection({
                                            placeHolder: "Search",
                                            activeTab: INVENTORY_TAB,
                                        })}
                                        <InventoryListing
                                            showNoResult={showNoResult}
                                            handleSort={handleInventorySort}
                                            itemList={itemList}
                                            onItemEdit={onItemEdit}
                                            onItemDelete={onItemDelete}
                                            sort={inventorySort}
                                            emptyItemHtml={emptyItemHtml}
                                            loading={loading}
                                        />
                                    </Tab>

                                    <Tab
                                        eventKey={WANT_AD_TAB}
                                        title={
                                            <React.Fragment>
                                                Want-Listings
                                                <Badge>{dashboardCount?.wantAd}</Badge>
                                            </React.Fragment>
                                        }
                                    >
                                        {dashboardHeaderSection({
                                            placeHolder: "Search",
                                            activeTab: WANT_AD_TAB,
                                        })}
                                        <WantAdListing
                                            showNoResult={showNoResult}
                                            handleSort={handleWantAdSort}
                                            itemList={itemList}
                                            onItemEdit={onItemEdit}
                                            onItemDelete={onItemDelete}
                                            sort={wantAdSort}
                                            emptyItemHtml={emptyItemHtml}
                                            loading={loading}
                                        />
                                    </Tab>
                                </Tabs>
                            </div>
                        </div>
                    </div>
                </div>
            </PageWrapper>

            <AddNewClientModal
                show={show}
                onHide={handleClose}
                addClient={addClientData}
            />
            <DeleteModal
                show={deleteModalShow}
                onHide={() => hideDeleteModal()}
                title={_.get(deleteButtonProps, "title", "")}
                message={_.get(deleteButtonProps, "message", "")}
                buttonText={_.get(deleteButtonProps, "btnText", "")}
                handleConfirm={() => handleDeleteItem()}
            />

            <AgentDashboardModal
                show={tutorialModalShow}
                onHide={() => hideTutorialModal()}
                title={_.get(deleteButtonProps, "title", "")}
                message={_.get(deleteButtonProps, "message", "")}
                buttonText={_.get(deleteButtonProps, "btnText", "")}
                handleConfirm={() => startTutorial()}
            />
        </>
    );
};

export default AgentDashboard;
